// Class-legal gear options per slot — the data behind the Character Builder's gear
// dropdowns. Mirrors the on-card "Edit gear" logic currently living inline in
// inlineUnit.vue (weaponTypes/slotItems); kept here so the builder can drive its
// own dropdowns and write gear into its unit. (A future cleanup could point
// inlineUnit at this lib too — the logic is intentionally identical.)
//
// weaponTypes()/slotItems() are pure (take the class record + support skill); only
// the item/type pools are read from the bundled snapshots, same as inlineUnit.
import itemsBackup from '../data/items.backup.json';
import classStats from '../data/classStats.json';

const ITEMS = Array.isArray(itemsBackup) ? itemsBackup : Object.values(itemsBackup).find(Array.isArray);

// type → sorted item names; the full weapon-type pool; Equip-X → weapon type.
const ITEM_BY_TYPE = {};
for (const it of ITEMS) (ITEM_BY_TYPE[it.type] ||= []).push(it.name);
for (const t of Object.keys(ITEM_BY_TYPE)) ITEM_BY_TYPE[t].sort();
export const ALL_WEAPON_TYPES = [...new Set(ITEMS.filter((i) => i.slot === 'hand' && i.type !== 'Shield').map((i) => i.type))];
export const EQUIP_SKILL_TYPE = {
  'Equip Knife': 'Knife', 'Equip Sword': 'Sword', 'Equip Axe': 'Axe',
  'Equip Polearm': 'Polearm', 'Equip Bow': 'Bow', 'Equip Gun': 'Gun',
};

// The classStats record (.M/.F) for a class+gender — preferred-arms / legal equip
// types. {} for unknown classes (e.g. monsters), so callers degrade gracefully.
export function classGearRecord(unitClass, gender) {
  const c = classStats[unitClass];
  const g = (gender || 'Male')[0];
  return (c && (c[g] || c.M || c.F)) || {};
}

// Legal weapon types for the class (expanded by any Equip-X support), or the whole
// pool when "show all" unlocks it.
export function weaponTypes(rec, supportSkill, showAll) {
  if (showAll) return ALL_WEAPON_TYPES;
  let types = Object.keys((rec && rec.weapons) || {});
  const eq = EQUIP_SKILL_TYPE[supportSkill];
  if (eq && rec && rec.equipArms && rec.equipArms[eq]) types = [...new Set([...types, ...Object.keys(rec.equipArms[eq])])];
  return types;
}

// Compact stat summary for an item record (from itemschanged) — e.g.
// "WP22 WEv35 Haste" / "HP+25 MP+29 MA+1". Shared by the gear picker + slot buttons.
export function itemStatSummary(it) {
  if (!it) return '';
  const p = [];
  if (it.wp) p.push('WP' + it.wp);
  const add = (k, lbl) => { if (it[k]) p.push(lbl + (it[k] > 0 ? '+' : '') + it[k]); };
  add('hp', 'HP'); add('mp', 'MP'); add('pa', 'PA'); add('ma', 'MA');
  add('speed', 'Sp'); add('move', 'Mv'); add('jump', 'Jp');
  if (it.pevade) p.push('Ev' + it.pevade);
  if (it.mevade) p.push('MEv' + it.mevade);
  if (it.wevade) p.push('WEv' + it.wevade);
  if (it.element) p.push(it.element);
  const status = (a) => (Array.isArray(a) ? a.map((s) => (s && (s.name || s))).filter(Boolean) : []);
  const st = [...status(it.permstatus), ...status(it.initstatus)];
  if (st.length) p.push(st.slice(0, 2).join('/'));
  return p.join(' ');
}

// Grouped item options for a slot: [{ label: type, items: [names] }] (empty groups
// dropped). Offhand = shield (if the class can) + a 2nd weapon (Dual Wield); show-all
// unlocks everything.
export function slotItems(slot, rec, supportSkill, showAll) {
  const group = (types) => types.map((t) => ({ label: t, items: ITEM_BY_TYPE[t] || [] })).filter((g) => g.items.length);
  const hasEquipShield = supportSkill === 'Equip Shield';
  if (slot === 'Mainhand') {
    const out = group(weaponTypes(rec, supportSkill, showAll));
    if (hasEquipShield && !showAll && rec.noWeapons) out.push(...group(['Shield']));
    return out;
  }
  if (slot === 'Head') return group(showAll ? ['Hat', 'Helmet'] : ((rec.heads && rec.heads.length) ? rec.heads : ['Hat', 'Helmet']));
  if (slot === 'Armor') return group(showAll ? ['Clothing', 'Armor', 'Robe'] : ((rec.bodies && rec.bodies.length) ? rec.bodies : ['Clothing', 'Robe', 'Armor']));
  if (slot === 'Accessory') return group(['Accessory']);
  if (slot === 'Offhand') {
    const out = [];
    if (showAll || rec.shield || hasEquipShield) out.push(...group(['Shield']));
    if (showAll || supportSkill === 'Dual Wield') out.push(...group(weaponTypes(rec, supportSkill, showAll)));
    return out;
  }
  return [];
}
